import React, { useState, useEffect } from 'react';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import Select from 'react-select';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { fetchPaymentsReports, fetchCustomers } from '../../utils/api/api';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Layout from '../../elements/Layout';
import Modal from '../../elements/Modal';
import { useNavigate } from 'react-router-dom';

const PaymentReportsPage = () => {
    const [filters, setFilters] = useState({
        payment_method: '',
        customer_id: '',
        is_manual: '',
        date_range: ['', ''],
    });
    const [customers, setCustomers] = useState([]);
    const [reports, setReports] = useState([]);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const loadCustomers = async () => {
            try {
                const response = await fetchCustomers({ page: 0, pageSize: 0 });
                const formattedCustomers = response.customers.map((customer) => ({
                    value: customer.id,
                    label: `${customer.first_name} ${customer.last_name}`,
                }));
                setCustomers(formattedCustomers);
            } catch (error) {
                console.error('Failed to fetch customers.', error);
            }
        };
        loadCustomers();
    }, []);

    const fetchReports = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchPaymentsReports({ ...filters, page, pageSize });
            setReports(data.paymentReports || []);
            setTotalPages(data.meta.totalPages);
        } catch (err) {
            setError('Failed to fetch reports. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [filters, page, pageSize]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const printPDF = async () => {
        setLoading(true);
        try {
            const allData = await fetchPaymentsReports({ ...filters, page: 1, pageSize: 1000 });
            const reportsData = reports;

            if (!reportsData || reportsData.length === 0) {
                alert("No payment reports available.");
                setLoading(false);
                return;
            }

            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
            });

            doc.setFontSize(14);
            doc.text("Payment Reports", 14, 10);

            const tableColumn = [
                { header: "#", dataKey: "index" },
                { header: "Customer", dataKey: "customer" },
                { header: "Method", dataKey: "method" },
                { header: "Transaction ID", dataKey: "transaction_id" },
                { header: "Amount (KES)", dataKey: "amount" },
                { header: "Date", dataKey: "date" },
                { header: "Created By", dataKey: "created_by" },
                { header: "Is Manual", dataKey: "is_manual" }
            ];

            const tableRows = reportsData.map((report, index) => ({
                index: index + 1,
                customer: `${report?.customer?.first_name || "N/A"} ${report?.customer?.last_name || ""}`.trim(),
                method: report.method,
                transaction_id: report.transaction_id || "N/A",
                amount: Number(report.amount).toLocaleString("en-US", { style: "currency", currency: "KES" }),
                date: moment(report.createdAt).format("MMM DD, YYYY, hh:mm A"),
                created_by: report.createdBy
                    ? `${report.createdBy.first_name} ${report.createdBy.last_name}`
                    : "Automated",
                is_manual: report.is_manual ? "Yes" : "No",
            }));

            autoTable(doc, {
                columns: tableColumn,
                body: tableRows,
                startY: 20,
                styles: {
                    fontSize: 10,
                    cellPadding: 3,
                    overflow: "linebreak",
                },
                columnStyles: {
                    0: { cellWidth: 10 },
                    1: { cellWidth: 35 }, 
                    2: { cellWidth: 25 }, 
                    3: { cellWidth: 40 }, 
                    4: { cellWidth: 25 },
                    5: { cellWidth: 35 },
                    6: { cellWidth: 35 },
                    7: { cellWidth: 20 },
                },
                headStyles: {
                    fillColor: [22, 160, 133],
                    textColor: 255,
                    fontStyle: "bold",
                },
                alternateRowStyles: {
                    fillColor: [240, 240, 240],
                },
            });

            doc.save("PaymentReports.pdf");
        } catch (error) {
            alert("Error generating PDF.");
        } finally {
            setLoading(false);
        }
    };

    const handleSelectChange = (name, value) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDateRangeChange = (ranges) => {
        const { startDate, endDate } = ranges.selection;
        const formattedRange = [
            moment(startDate).format('YYYY-MM-DD'),
            moment(endDate).format('YYYY-MM-DD')
        ];
        setDateRange([ranges.selection]);
        setFilters((prev) => ({
            ...prev,
            date_range: formattedRange,
        }));
    };

    const exportToExcel = () => {
        if (reports.length === 0) {
            alert('No reports available to export.');
            return;
        }

        const data = reports.map((report, index) => ({
            '#': index + 1,
            Customer: `${report?.customer?.first_name} ${report?.customer?.last_name}`,
            Method: report.method,
            'Transaction ID': report.transaction_id,
            Amount: Number(report.amount).toLocaleString('en-US', { style: 'currency', currency: 'KES' }),
            Date: moment(report.createdAt).format('MMM DD, YYYY, HH:MM A'),
            'Created By': report.createdBy
                ? `${report.createdBy.first_name} ${report.createdBy.last_name}`
                : 'N/A',
            'Is Manual': report.is_manual ? 'Yes' : 'No',
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Payment Reports');
        XLSX.writeFile(workbook, 'PaymentReports.xlsx');
    };

    return (
        <Layout>
            <div className="min-h-screen">
                <div className="w-full border-b flex items-center justify-between pb-8 -mt-2 mb-6">
                    <p className="text-[17px] font-semibold text-primary">Payments Reports</p>
                    <div className="flex justify-end gap-2 items-center">
                        <button
                            onClick={() => setIsFilterModalOpen(true)}
                            className="px-6 py-1 bg-primary text-[14px] text-white rounded-md"
                        >
                            Filters
                        </button>
                        <button
                            onClick={printPDF}
                            className="px-6 py-1 bg-primary text-[14px] text-white rounded-md"
                        >
                            Print
                        </button>
                        <button
                            onClick={exportToExcel}
                            className="px-6 py-1 bg-primary text-[14px] text-white rounded-md"
                        >
                            Export to Excel
                        </button>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-md">
                    {error && <p className="text-red-600">{error}</p>}
                    {!loading && reports.length === 0 && <p className="text-gray-500">No reports found.</p>}
                    {loading && <p className="text-gray-500">Loading...</p>}
                    {reports.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full table-auto border-collapse rounded-md">
                                <thead>
                                    <tr className="bg-gray-50 text-black">
                                        <th className="border-b text-[13px] font-semibold uppercase border-gray-200 text-start py-2">#</th>
                                        <th className="border-b text-[13px] font-semibold uppercase border-gray-200 text-start px-2 py-2">Customer</th>
                                        <th className="border-b text-[13px] font-semibold uppercase border-gray-200 text-start px-2 py-2">Method</th>
                                        <th className="border-b text-[13px] font-semibold uppercase border-gray-200 text-start px-2 py-2">TRX Id</th>
                                        <th className="border-b text-[13px] font-semibold uppercase border-gray-200 text-start px-2 py-2">Amount</th>
                                        <th className="border-b text-[13px] font-semibold uppercase border-gray-200 text-start px-2 py-2">Date</th>
                                        <th className="border-b text-[13px] font-semibold uppercase border-gray-200 text-start px-2 py-2">Created By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((report, index) => (
                                        <tr key={report.id} className="hover:bg-gray-50 text-black">
                                            <td className="border-b text-[13px] text-gray-900  text border-gray-200 py-2 whitespace-nowrap">{index + 1}</td>
                                            <td onClick={() => navigate(`/dashboard/customers/${report?.customer?.id}/view`)} className="border-b text-[13px] uppercase text-gray-900  text border-gray-200 px-2 py-2 whitespace-nowrap cursor-pointer hover:text-secondary">
                                                {`${report?.customer?.first_name || ''} ${report?.customer?.last_name || ''}`
                                                    .split(" ")
                                                    .slice(0, 2)
                                                    .join(" ")}
                                            </td>
                                            <td className="border-b text-[13px] text-gray-900 text border-gray-200 px-2 py-2 whitespace-nowrap">{report.method}</td>
                                            <td className="border-b text-[13px] text-gray-900 text border-gray-200 px-2 py-2 whitespace-nowrap">
                                                {report?.transaction_id?.slice(0, 11)}
                                            </td>
                                            <td className="border-b text-[13px] text-gray-900 text border-gray-200 px-2 py-2 whitespace-nowrap">
                                                {Number(report.amount).toLocaleString('en-US', { style: 'currency', currency: 'KES' })}
                                            </td>
                                            <td className="border-b text-[13px] text-gray-900 text border-gray-200 px-2 py-2 whitespace-nowrap">
                                                {moment(report.createdAt).format('MMM DD, YYYY, HH:MM A')}
                                            </td>
                                            <td className="border-b text-[13px] text-gray-900 text border-gray-200 px-2 py-2 whitespace-nowrap">
                                                {report.createdBy
                                                    ? `${report.createdBy.first_name} ${report.createdBy.last_name}`
                                                    : 'Automated'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="flex items-center gap-4 mt-4">
                                {/* Page Size Selection */}
                                <label className="text-sm">Page Size:</label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    className="px-2 py-1 border rounded-md"
                                >
                                    {[10, 20, 50, 100].map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>

                                {/* Previous Page Button */}
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page === 1}
                                    className="px-4 py-2 bg-gray-100 rounded-md disabled:opacity-50"
                                >
                                    Previous
                                </button>

                                {/* Page Dropdown */}
                                <select
                                    value={page}
                                    onChange={(e) => handlePageChange(Number(e.target.value))}
                                    className="px-2 py-1 border rounded-md"
                                >
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                                        <option key={pageNumber} value={pageNumber}>
                                            {pageNumber}
                                        </option>
                                    ))}
                                </select>

                                <p className="text-sm text-gray-600">of {totalPages}</p>

                                {/* Next Page Button */}
                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page === totalPages}
                                    className="px-4 py-2 bg-gray-100 rounded-md disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                </div>
                {isFilterModalOpen && (
                    <Modal onClose={() => setIsFilterModalOpen(false)} title="Filter Reports">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium">Payment Method</label>
                                <select
                                    name="payment_method"
                                    value={filters.payment_method}
                                    onChange={(e) => handleSelectChange('payment_method', e.target.value)}
                                    className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                >
                                    <option value="">All</option>
                                    <option value="MPESA">MPESA</option>
                                    <option value="BANK_TRANSFER">Bank Transfer</option>
                                    <option value="CHEQUE">Cheque</option>
                                    <option value="VISA">Visa</option>
                                    <option value="CASH">Cash</option>
                                    <option value="WORLD_REMIT">World Remit</option>
                                    <option value="RTGS">RTGS</option>
                                    <option value="CREDIT_NOTE">Credit Note</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium">Customer</label>
                                <Select
                                    options={customers}
                                    onChange={(selected) => handleSelectChange('customer_id', selected ? selected.value : '')}
                                    isClearable
                                    placeholder="Select customer"
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium">Is Manual?</label>
                                <select
                                    name="is_manual"
                                    value={filters.is_manual}
                                    onChange={(e) => handleSelectChange('is_manual', e.target.value === '' ? '' : e.target.value === 'true')}
                                    className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                >
                                    <option value="">All</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium">Date Range</label>
                                <DateRangePicker
                                    ranges={dateRange}
                                    onChange={handleDateRangeChange}
                                    showSelectionPreview={true}
                                    moveRangeOnFirstSelection={false}
                                    className="w-full"
                                />
                            </div>

                            <div className="flex justify-end space-x-4">
                                <button
                                    onClick={() => setFilters({ payment_method: '', customer_id: '', is_manual: '', date_range: ['', ''] })}
                                    className="px-4 py-2 bg-gray-400 text-white rounded-md"
                                >
                                    Reset
                                </button>
                                <button
                                    onClick={() => {
                                        setIsFilterModalOpen(false);
                                        fetchReports();
                                    }}
                                    className="px-4 py-2 bg-primary text-white rounded-md"
                                >
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}

            </div>
        </Layout>
    );
};

export default PaymentReportsPage;
